import {
    AccountTree as AccountTreeIcon,
    Add as AddIcon,
    Logout as LogoutIcon,
    Menu as MenuIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Chip,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { signOut } from 'aws-amplify/auth';
import { useTaxonomySync } from '../../hooks/useTaxonomySync';
import { detectEnvironment } from '../../utils/environment.config';

const drawerWidth = 240;

// Environment detection using comprehensive detection logic
const getCurrentEnvironment = () => {
  return detectEnvironment();
};

// Version from package.json
const APP_VERSION = process.env.REACT_APP_VERSION || '1.1.6';

// Define navigation items (core only; Dashboard, Settings, Taxonomy Browser removed)
const navigationItems = [
  { text: 'Browse Assets', icon: <SearchIcon />, path: '/search-assets' },
  { text: 'Register Asset', icon: <AddIcon />, path: '/register-asset' },
  { text: 'Taxonomy', icon: <AccountTreeIcon />, path: '/taxonomy' },
];

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { authStatus, user: authenticatorUser } = useAuthenticator();
  const [userInfo, setUserInfo] = useState<{ username?: string; email?: string } | null>(null);
  useTaxonomySync();

  // Fetch user info from Amplify session
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const session = await fetchAuthSession();
        if (session.tokens?.idToken) {
          // Decode JWT to get user info
          const payload = JSON.parse(atob(session.tokens.idToken.toString().split('.')[1]));
          setUserInfo({
            username: payload['cognito:username'] || payload.email?.split('@')[0] || '',
            email: payload.email || '',
          });
        } else if (authenticatorUser) {
          // Fallback to authenticator user
          setUserInfo({
            username: authenticatorUser.username || '',
            email: authenticatorUser.signInDetails?.loginId || '',
          });
        }
      } catch (error) {
        console.warn('Failed to fetch user info:', error);
      }
    };

    if (authStatus === 'authenticated') {
      fetchUserInfo();
    } else {
      setUserInfo(null);
    }
  }, [authStatus, authenticatorUser]);

  const handleLogout = async () => {
    try {
      await signOut();
      // Redirect to home - Authenticator will show login UI
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      // Fallback: redirect anyway
      navigate('/');
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
          NNA Registry
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Release {APP_VERSION}
          </Typography>
          <Chip
            label={getCurrentEnvironment().toUpperCase()}
            size="small"
            color={
              getCurrentEnvironment() === 'production' ? 'success' :
              getCurrentEnvironment() === 'staging' ? 'warning' : 'error'
            }
            sx={{ fontWeight: 600, fontSize: '0.75rem', alignSelf: 'center' }}
          />
        </Box>
      </Box>
      <Divider />
      <List sx={{ px: 1.5, py: 1 }}>
        {navigationItems.map(item => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (mobileOpen) handleDrawerToggle();
              }}
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        color="default"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <IconButton
            color="default"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Spacer to push user info and logout to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right-aligned user info */}
          {userInfo && (
            <Typography 
              variant="body2" 
              sx={{ 
                mr: 2,
                fontSize: '0.9rem',
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              • {userInfo.username || userInfo.email || 'User'}
            </Typography>
          )}

          {/* Logout button - only show when authenticated */}
          {authStatus === 'authenticated' && (
            <Tooltip title="Logout">
              <IconButton color="primary" onClick={handleLogout} aria-label="Logout">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>
      {/* Mobile temporary drawer - opens from hamburger */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>
      {/* Desktop permanent sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'background.paper',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
